import React, { useState, useEffect } from 'react';
import './FAQ.css';

interface FAQSection {
  id: string;
  title: string;
  subsections: FAQSubsection[];
}

interface FAQSubsection {
  id: string;
  title: string;
  content: string;
  platformContent?: {
    universal: InstallationContent;
  };
}

interface InstallationContent {
  video?: string;
  steps: InstallationStep[];
}

interface InstallationStep {
  number: number;
  title: string;
  description: string;
  image?: string;
}

const faqSections: FAQSection[] = [
  {
    id: 'installation',
    title: 'Installation',
    subsections: [
      {
        id: 'how-to-install',
        title: 'How to Install',
        content: 'Installing Substyle is simple and works the same way on both Mac and Windows!',
        platformContent: {
          universal: {
            steps: [
              {
                number: 1,
                title: 'Visit Adobe Exchange',
                description: 'Visit the <a href="https://exchange.adobe.com/apps/cc/204399/substyle" target="_blank" rel="noopener noreferrer">Adobe Exchange page</a> and install Substyle — it\'s free.',
              },
              {
                number: 2,
                title: 'Open Substyle in Premiere Pro',
                description: 'Open Premiere Pro, then go to <strong>Window &gt; Extensions &gt; Substyle</strong>.',
              },
              {
                number: 3,
                title: 'Start creating',
                description: 'You\'re ready to go. Free includes 200 free monthly credits (~200 seconds of subtitling). When you hit the limit, upgrade to Pro in the extension.',
              }
            ]
          }
        }
      },
      {
        id: 'compatibility',
        title: 'Compatibility',
        content: 'Substyle is compatible with Premiere Pro 2024 and 2025. It may support older versions of Premiere Pro, but we do not guarantee compatibility.'
      }
    ]
  },
  {
    id: 'pricing',
    title: 'Pricing & Credits',
    subsections: [
      {
        id: 'free-vs-pro',
        title: 'Plans & Credits',
        content: 'Free includes 200 free monthly credits (~200 seconds of subtitling) that reset each month. Pro is $9.99/month with 10,000 credits every month. Ultra is $29.99/month with 50,000 credits every month. Credits reset monthly on all plans. Upgrade from inside the extension or on the pricing page when you need more.'
      },
      {
        id: 'what-are-credits',
        title: 'What are credits?',
        content: 'Credits measure how much audio you can subtitle. Roughly 1 credit ≈ 1 second of subtitling. Actual usage can vary based on project complexity.'
      },
      {
        id: 'license-terms',
        title: 'License Terms',
        content: 'Your Substyle license allows you to install and use the software on 1 device that you own or control. To install on multiple devices, you would need a license for each device.'
      },
      {
        id: 'refund-policy',
        title: 'Refund Policy',
        content: 'While we do not offer refunds, we do refund based on our own discretion. All sales are final.'
      }
    ]
  },
  {
    id: 'contact',
    title: 'Contact',
    subsections: [
      {
        id: 'report-bug',
        title: 'Report a Bug',
        content: 'Found an issue? Let us know and we\'ll fix it! <a href="/bugreport">Submit a Bug Report</a>'
      },
      {
        id: 'contact-us',
        title: 'Contact Us',
        content: 'Can\'t find the answer you\'re looking for? Our support team is here to help. Email us at support@codec422.com.'
      }
    ]
  }
];

const FAQ: React.FC = () => {
  const [selectedSubsection, setSelectedSubsection] = useState<string>('how-to-install');

  const currentSubsection = faqSections
    .flatMap(section => section.subsections)
    .find(sub => sub.id === selectedSubsection);

  const handleSubsectionClick = (subsectionId: string) => {
    setSelectedSubsection(subsectionId);
  };

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="faq-page">
      {/* FAQ Hero Section */}
      <section className="faq-hero">
        <div className="container">
          <div className="faq-hero-content">
            <h1>Need help? We've got you covered.</h1>
            <p>Find all you need to know about Substyle here.</p>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="faq-content">
        <div className="container">
          <div className="faq-layout">
            {/* Left Sidebar */}
            <div className="faq-sidebar">
              <nav className="faq-nav">
                {faqSections.map(section => (
                  <div key={section.id} className="nav-section">
                    <div className="nav-section-header">
                      {section.title}
                    </div>
                    <div className="nav-subsections">
                      {section.subsections.map(subsection => (
              <button 
                          key={subsection.id}
                          className={`nav-subsection-btn ${selectedSubsection === subsection.id ? 'active' : ''}`}
                          onClick={() => handleSubsectionClick(subsection.id)}
                        >
                          {subsection.title}
              </button>
                      ))}
                    </div>
                  </div>
                ))}
              </nav>
            </div>

            {/* Right Content Area */}
            <div className="faq-main-content">
              {currentSubsection && (
                <div className="content-section">
                  <h2>{currentSubsection.title}</h2>
                  
                  {currentSubsection.platformContent ? (
                    <div className="installation-content">
                      {/* Video Section */}
                      {currentSubsection.platformContent.universal.video && (
                        <div className="video-section">
                          <div className="video-container">
                            <iframe
                              src={currentSubsection.platformContent.universal.video}
                              title="Substyle Installation"
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            ></iframe>
                          </div>
                        </div>
                      )}

                      {/* Installation Steps */}
                      <div className="installation-steps">
                        <h3>Step-by-Step Installation</h3>
                        {currentSubsection.platformContent.universal.steps.map((step) => (
                          <div key={step.number} className="installation-step">
                            <div className="step-header">
                              <div className="step-number">{step.number}</div>
                              <h4>{step.title}</h4>
                            </div>
                            <div className="step-content">
                              <p dangerouslySetInnerHTML={{ __html: step.description }}></p>
                              {step.image && (
                                <div className="step-image">
                                  <img src={step.image} alt={`Step ${step.number}: ${step.title}`} />
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="content-text">
                      {currentSubsection.content.split('\n').map((paragraph, index) => (
                        <p key={index} dangerouslySetInnerHTML={{ __html: paragraph }}></p>
                      ))}
                    </div>
                  )}
            </div>
          )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ; 